import type {
  ListWorkspaceMembershipsFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  PrimeMemberByPublicIdFn,
  PrimeProviderFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import type { Member } from '@/features/iam/members/Member'
import type {
  FetchInventoryItemsByIdsFn,
  ListInventoryItemsFn,
} from '@/features/inventory'
import type { FetchJobsFn, PrimeJobFn } from '@/features/jobs'
import type { CanViewPayoutsAdminFn } from '@/features/payouts'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { CursorPaged } from '@/infra/paging'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import type { Observable } from 'rxjs'
import type { Bundle } from '@/features/admin/bundle/Bundle'
import type { ListBundlesQuery } from '@/features/admin/bundle/BundleStore'

/**
 * Options for {@link AdminFeature}.
 */
export interface AdminFeatureOptions {
  token$: Observable<string>
  workRealtime$: Observable<RealtimeEvent>
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  currentMember$: Observable<Member>
  getCurrentMember: GetCurrentMemberFn
  primeUser: PrimeUserFn
  primeProvider: PrimeProviderFn
  primeWorkspace: PrimeWorkspaceFn
  primeMemberByPublicId: PrimeMemberByPublicIdFn
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  listWorkspaceMemberships: ListWorkspaceMembershipsFn
  primeJob: PromiseLike<PrimeJobFn>
  fetchJobs: FetchJobsFn
  searchInventoryItems: ListInventoryItemsFn
  fetchInventoryItemsByIds: FetchInventoryItemsByIdsFn
  canViewPayoutsAdmin: CanViewPayoutsAdminFn
}

/**
 * Lists the bundles of a workspace.
 */
export type ListBundlesFn = (
  query: ListBundlesQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<Bundle>>

/**
 * Features relating to the admin pages.
 */
export type AdminFeature = ReturnType<typeof AdminFeature>

export function AdminFeature(options: AdminFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalAdminFeature(options),
  )

  const listBundles: ListBundlesFn = internal.wrap((m) => m.listBundles)

  return {
    // Internal
    internal,

    // Public
    listBundles,
  }
}
